import { useEffect, useMemo, useState } from 'react';

import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';

import { getMetgesActivitatUrl, getMetgesUrl } from './api';

import { medicalServicesSegment, medicalFolderSegment } from './localizedSectionPath';

export const QUADRE_MEDIC_SEGMENT = 'serveis-medics/quadre-medic';

export const metgeSlugFromCognomsNom = (cognomsNom) => {
  if (!cognomsNom) {
    return '';
  }
  return cognomsNom
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
};

export const assignMetgeSlugs = (list) => {
  const sorted = [...(list || [])].sort((a, b) =>
    (a.CognomsNom || '').localeCompare(b.CognomsNom || '', 'ca'),
  );
  const slugUsage = {};
  const slugByCognomsNom = new Map();

  sorted.forEach((item) => {
    const baseSlug = metgeSlugFromCognomsNom(item.CognomsNom);
    const usage = slugUsage[baseSlug] || 0;
    slugUsage[baseSlug] = usage + 1;
    const slug = usage === 0 ? baseSlug : `${baseSlug}-${usage + 1}`;
    slugByCognomsNom.set(item.CognomsNom, slug);
  });

  return (list || []).map((item) => ({
    ...item,
    slug: slugByCognomsNom.get(item.CognomsNom),
  }));
};

export const findMetgeBySlug = (list, slug) =>
  (list || []).find((item) => item.slug === slug);

export const findMetgeByCognomsNom = (list, cognomsNom) =>
  (list || []).find((item) => item.CognomsNom === cognomsNom);

export const resolveMetgeSlugFromCognomsNom = (list, cognomsNom) =>
  findMetgeByCognomsNom(assignMetgeSlugs(list), cognomsNom)?.slug || null;

export const getQuadreMedicBasePath = (language) =>
  `/${language}/${medicalServicesSegment(language)}/${medicalFolderSegment('quadre-medic', language)}`;

export const getQuadreMedicMetgePath = (language, slug) =>
  `${getQuadreMedicBasePath(language)}/${slug}`;

export const getMetgeSeoMetadata = (metge, language, publicUrl = '') => {
  if (!metge) {
    return null;
  }

  const title = `${metge.CognomsNom} | Quadre mèdic | Clínica Sant Josep`;
  const descriptionParts = [];
  if (metge.Especialitat?.length) {
    descriptionParts.push(metge.Especialitat.join(', '));
  }
  if (metge.carrec) {
    descriptionParts.push(metge.carrec);
  }

  let description = descriptionParts.join('. ');
  if (description.length > 160) {
    description = `${description.slice(0, 157)}...`;
  }

  const path = getQuadreMedicMetgePath(language, metge.slug);
  const canonical = `${publicUrl.replace(/\/$/, '')}${path}`;

  return { title, description, canonical, path };
};

export const fetchAllItems = async (url) => {
  const items = [];
  let nextUrl = url;

  while (nextUrl) {
    const response = await fetch(nextUrl, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
      break;
    }
    const data = await response.json();
    items.push(...(data.items || []));
    nextUrl = data.batching?.next || null;
  }

  return items;
};

const normalizeMetgeId = (metgeId) => {
  if (!metgeId?.['@id']) {
    return metgeId;
  }
  return {
    ...metgeId,
    '@id': metgeId['@id'].replace('@items_cached', metgeId.id || ''),
  };
};

const findMetgeProfile = (metges, cognomsNom) =>
  (metges || []).find(
    (metge) => metge.title === cognomsNom || metge.cognomsnom === cognomsNom,
  );

export const getMetgeImageUrl = (metge) => {
  if (!metge) {
    return null;
  }
  const image = metge.image;
  if (image?.scales?.preview?.download) {
    return flattenToAppURL(image.scales.preview.download);
  }
  if (image?.scales?.mini?.download) {
    return flattenToAppURL(image.scales.mini.download);
  }
  if (image?.download) {
    return flattenToAppURL(image.download);
  }
  return null;
};

const normalizeFormacions = (formacions) => {
  if (!formacions) {
    return [];
  }
  if (Array.isArray(formacions)) {
    return formacions.filter(Boolean);
  }
  return [formacions].filter(Boolean);
};

const createGroupedEntry = (cognomsNom, index, metgeProfile) => ({
  index,
  CognomsNom: cognomsNom,
  Cobertura: [],
  Especialitat: [],
  Lloc: [],
  Societat: [],
  Unitat: [],
  formacions: normalizeFormacions(metgeProfile?.formacions),
  carrec: metgeProfile?.carrec || null,
  any_incorporacio: metgeProfile?.any_incorporacio || null,
  reflexio: metgeProfile?.reflexio || null,
  image: getMetgeImageUrl(metgeProfile),
});

export const buildEspecialitatsList = (metgesactivitat) => {
  const titles = new Set();
  (metgesactivitat || []).forEach((item) => {
    const title = item.especialitatid?.title;
    if (title) {
      titles.add(title);
    }
  });
  return Array.from(titles).sort((a, b) => a.localeCompare(b, 'ca'));
};

export const buildQuadreMedicList = (metges, metgesactivitat, especialitat) => {
  const metgesBaixa = new Set(
    (metges || [])
      .filter((metge) => metge.baixa)
      .map((metge) => normalizeMetgeId(metge)['@id']),
  );

  let filtered = (metgesactivitat || []).filter((item) => {
    const metgeId = item.metgeid;
    return (
      metgeId &&
      metgeId.review_state !== 'private' &&
      !metgesBaixa.has(normalizeMetgeId(metgeId)['@id'])
    );
  });

  if (especialitat) {
    filtered = filtered.filter(
      (item) =>
        item.especialitatid && item.especialitatid.title === especialitat,
    );
  }

  filtered.sort((a, b) => {
    const left = a.metgeid?.title || '';
    const right = b.metgeid?.title || '';
    return left.localeCompare(right, 'ca');
  });

  const grouped = [];

  filtered.forEach((item) => {
    const cognomsNom = item.metgeid?.title;
    if (!cognomsNom) {
      return;
    }

    let existing = grouped.find((entry) => entry.CognomsNom === cognomsNom);
    if (!existing) {
      existing = createGroupedEntry(
        cognomsNom,
        grouped.length,
        findMetgeProfile(metges, cognomsNom),
      );
      grouped.push(existing);
    }

    const appendUnique = (field, value) => {
      if (value && !existing[field].includes(value)) {
        existing[field].push(value);
      }
    };

    appendUnique('Cobertura', item.coberturaid?.title);
    appendUnique('Especialitat', item.especialitatid?.title);
    appendUnique('Lloc', item.llocid?.title || item.llocconsultaid?.title);
    appendUnique('Societat', item.societatid?.title);
    appendUnique('Unitat', item.unitatid?.title);
  });

  return assignMetgeSlugs(grouped);
};

export const fetchQuadreMedicList = async (language) => {
  const [metges, metgesactivitat] = await Promise.all([
    fetchAllItems(getMetgesUrl(language)),
    fetchAllItems(getMetgesActivitatUrl(language)),
  ]);
  return buildQuadreMedicList(metges, metgesactivitat);
};

export const filterQuadreMedicList = (
  list,
  { especialitat = '', metgeQuery = '' } = {},
) =>
  (list || []).filter((item) => {
    const matchesEspecialitat = especialitat
      ? item.Especialitat.includes(especialitat)
      : true;
    const matchesMetge = metgeQuery
      ? item.CognomsNom.toLowerCase().includes(metgeQuery.toLowerCase())
      : true;
    return matchesEspecialitat && matchesMetge;
  });

const useMedicalFetch = (language) => {
  const [loading, setLoading] = useState(true);
  const [metges, setMetges] = useState([]);
  const [metgesactivitat, setMetgesactivitat] = useState([]);

  useEffect(() => {
    if (!language) {
      setLoading(false);
      setMetges([]);
      setMetgesactivitat([]);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    Promise.all([
      fetchAllItems(getMetgesUrl(language)),
      fetchAllItems(getMetgesActivitatUrl(language)),
    ])
      .then(([metgesItems, activitatItems]) => {
        if (cancelled) {
          return;
        }
        setMetges(metgesItems);
        setMetgesactivitat(activitatItems);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [language]);

  return { loading, metges, metgesactivitat };
};

export const useQuadreMedicData = (language, especialitat) => {
  const { loading, metges, metgesactivitat } = useMedicalFetch(language);

  const quadreMedicList = useMemo(
    () => buildQuadreMedicList(metges, metgesactivitat, especialitat),
    [metges, metgesactivitat, especialitat],
  );

  return { loading, quadreMedicList };
};

export const useQuadreMedicPageData = (language) => {
  const { loading, metges, metgesactivitat } = useMedicalFetch(language);

  const quadreMedicList = useMemo(
    () => buildQuadreMedicList(metges, metgesactivitat),
    [metges, metgesactivitat],
  );

  const especialitatsList = useMemo(
    () => buildEspecialitatsList(metgesactivitat),
    [metgesactivitat],
  );

  return { loading, metges, quadreMedicList, especialitatsList };
};
